<template>
  <div class="team-page">
    <!-- ===== حالت لودینگ ===== -->
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
      <p>در حال دریافت اطلاعات تیم...</p>
    </div>

    <!-- ===== خطا ===== -->
    <div v-else-if="error" class="state-box error">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <router-link to="/" class="back-btn">بازگشت به خانه</router-link>
    </div>

    <div v-else-if="team" class="team-container">

      <!-- breadcrumb -->
      <div class="breadcrumb">
        <router-link to="/">خانه</router-link>
        <i class="fas fa-chevron-left"></i>
        <span>{{ faName }}</span>
      </div>

      <!-- ===== هدر تیم ===== -->
      <div class="team-hero">
        <div class="hero-main">
          <img v-if="team.logo" :src="team.logo" class="hero-logo" :alt="team.name"/>

          <div class="hero-info">
            <h1 class="hero-name">{{ faName }} <span v-if="team.isNational" class="nat-chip"><i class="fas fa-flag"></i> تیم ملی</span></h1>
            <div class="hero-meta">
              <span v-if="team.area?.name" class="meta-item">
                <img v-if="team.area.flag" :src="team.area.flag" class="flag"/>
                {{ faCountry(team.area.name) }}
              </span>
              <span v-if="team.venue?.name" class="meta-item">
                <i class="fas fa-stadium"></i> {{ team.venue.name }}
                <small v-if="team.venue.city">· {{ team.venue.city }}</small>
              </span>
              <span v-if="team.venue?.capacity" class="meta-item">
                <i class="fas fa-users"></i> ظرفیت {{ faNum(team.venue.capacity.toLocaleString('en-US').replace(/,/g, '،')) }}
              </span>
            </div>
          </div>
        </div>

        <!-- کارت سرمربی — کلیک: صفحهٔ اختصاصی مربی -->
        <router-link
            v-if="coach && coach.slug"
            :to="`/coach/${coach.slug}`"
            class="coach-card"
        >
          <i class="fas fa-user-tie"></i>
          <div class="coach-info">
            <span class="coach-label">سرمربی</span>
            <span class="coach-name">{{ coachNameFa(coach.name) }}</span>
          </div>
          <i class="fas fa-chevron-left coach-arrow"></i>
        </router-link>
        <div v-else-if="coach" class="coach-card">
          <i class="fas fa-user-tie"></i>
          <div class="coach-info">
            <span class="coach-label">سرمربی</span>
            <span class="coach-name">{{ coachNameFa(coach.name) }}</span>
          </div>
        </div>
      </div>

      <!-- اعلان سینک اسکواد -->
      <div v-if="squadSyncing" class="sync-banner">
        <i class="fas fa-sync-alt fa-spin"></i>
        در حال تکمیل اطلاعات بازیکنان این تیم... چند لحظه بعد صفحه را تازه‌سازی کنید.
      </div>

      <!-- ===== تب‌ها ===== -->
      <div class="tabs">
        <button
            v-for="t in tabs"
            :key="t.key"
            :class="['tab-btn', { active: activeTab === t.key }]"
            @click="switchTab(t.key)"
        >
          <i :class="['fas', t.icon]"></i> {{ t.label }}
        </button>
      </div>

      <!-- ---------- تب بازیکنان ---------- -->
      <div v-if="activeTab === 'players'" class="tab-content">
        <div v-if="playerGroups.length === 0" class="empty-state">
          <i class="fas fa-users"></i>
          <p>{{ squadSyncing ? 'در حال دریافت بازیکنان...' : 'فهرست بازیکنان ثبت نشده' }}</p>
        </div>

        <div v-for="group in playerGroups" :key="group.key" class="position-group">
          <h3 class="group-title">
            <i :class="['fas', groupIcon(group.key)]"></i>
            {{ group.label }}
            <small>{{ faNum(group.players.length) }} بازیکن</small>
          </h3>

          <div class="players-grid">
            <div
                v-for="p in group.players"
                :key="p.id"
                class="player-card"
                @click="$router.push(`/player/${p.slug}`)"
            >
              <!-- جای عکس: تصویر محلی src/assets/image/player/{slug}.jpg اگر موجود باشد -->
              <span class="player-number" v-if="p.shirtNumber">{{ faNum(p.shirtNumber) }}</span>
              <span class="player-avatar" :style="p.photo ? {} : { background: avatarColor(p.name) }">
                <img v-if="p.photo" :src="p.photo" :alt="p.name" loading="lazy">
                <template v-else>{{ initials(p.name) }}</template>
              </span>
              <span class="player-name">{{ faPlayerName(p.name) }}</span>
              <span class="player-sub">
                {{ faCountry(p.nationality) || '—' }}
                <template v-if="ageOf(p.birthDate)"> · {{ faNum(ageOf(p.birthDate)) }} ساله</template>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ---------- تب بازی‌ها ---------- -->
      <div v-if="activeTab === 'matches'" class="tab-content">

        <!-- پیش‌رو -->
        <template v-if="matches.upcoming.length">
          <h3 class="section-title"><span class="dot live-dot"></span> بازی‌های پیش‌رو</h3>
          <article
              v-for="m in matches.upcoming"
              :key="'u' + m.id"
              class="match-card"
              :class="{ live: isLive(m) }"
              @click="$router.push(`/match/${m.id}`)"
          >
            <div class="round-date">
              <span v-if="roundLabel(m.round)" class="round">{{ roundLabel(m.round) }}</span>
              <span class="kickoff">{{ faDate(m.kickoffAt) }}</span>
            </div>
            <div class="row">
              <div class="side">
                <img v-if="m.home?.team?.logo" :src="m.home.team.logo"/>
                <span :class="{ winner: m.home?.isWinner }">{{ faTeamName(m.home?.team?.name) }}</span>
              </div>
              <div class="center">
                <strong v-if="hasScore(m)">{{ m.home.score }} - {{ m.away.score }}</strong>
                <strong v-else>{{ faTime(m.kickoffAt) }}</strong>
                <small :class="{ 'live-label': isLive(m) }">{{ statusLabel(m.status) }}</small>
              </div>
              <div class="side">
                <span :class="{ winner: m.away?.isWinner }">{{ faTeamName(m.away?.team?.name) }}</span>
                <img v-if="m.away?.team?.logo" :src="m.away.team.logo"/>
              </div>
            </div>
          </article>
        </template>

        <!-- نتایج -->
        <template v-if="matches.recent.length">
          <h3 class="section-title"><span class="dot done-dot"></span> نتایج اخیر</h3>
          <article
              v-for="m in matches.recent"
              :key="'r' + m.id"
              class="match-card"
              @click="$router.push(`/match/${m.id}`)"
          >
            <div class="round-date">
              <span v-if="m.competition?.name" class="comp">{{ faLeagueName(m.competition.name) }}</span>
              <span class="kickoff">{{ faDate(m.kickoffAt) }}</span>
            </div>
            <div class="row">
              <div class="side">
                <img v-if="m.home?.team?.logo" :src="m.home.team.logo"/>
                <span :class="{ winner: m.home?.isWinner }">{{ faTeamName(m.home?.team?.name) }}</span>
              </div>
              <div class="center">
                <strong>{{ m.home?.score ?? 0 }} - {{ m.away?.score ?? 0 }}</strong>
                <small :class="resultClass(m)">{{ resultLabel(m) }}</small>
              </div>
              <div class="side">
                <span :class="{ winner: m.away?.isWinner }">{{ faTeamName(m.away?.team?.name) }}</span>
                <img v-if="m.away?.team?.logo" :src="m.away.team.logo"/>
              </div>
            </div>
          </article>
        </template>

        <div v-if="!matches.upcoming.length && !matches.recent.length" class="empty-state">
          <i class="fas fa-futbol"></i>
          <p>بازی‌ای ثبت نشده</p>
        </div>
      </div>

      <!-- ---------- تب آمار ---------- -->
      <div v-if="activeTab === 'stats'" class="tab-content">
        <div v-if="teamStatsLoading" class="empty-state">
          <i class="fas fa-chart-bar fa-spin"></i>
          <p>در حال محاسبهٔ آمار فصل...</p>
        </div>

        <div v-else-if="!teamStats || teamStats.empty" class="empty-state">
          <i class="fas fa-chart-bar"></i>
          <p>آماری برای این تیم در فصل جاری ثبت نشده</p>
        </div>

        <template v-else>
          <!-- خلاصهٔ فصل از جدول -->
          <div v-if="teamStats.summary" class="season-summary">
            <p v-if="teamStats.seasonInfo && teamStats.seasonInfo.competitionName" class="ss-source">
              <i class="fas fa-database"></i>
              منبع: {{ teamStats.seasonInfo.competitionName }}<template v-if="teamStats.seasonInfo.seasonName"> — فصل {{ teamStats.seasonInfo.seasonName }}</template>
            </p>
            <div class="ss-item">
              <b>{{ faNum(teamStats.summary.position) }}</b>
              <small>رتبه در جدول</small>
            </div>
            <div class="ss-item">
              <b>{{ faNum(teamStats.summary.points) }}</b>
              <small>امتیاز</small>
            </div>
            <div class="ss-item">
              <b>{{ faNum(teamStats.summary.played) }}</b>
              <small>بازی</small>
            </div>
            <div class="ss-item">
              <b>{{ faNum(teamStats.summary.won) }}-{{ faNum(teamStats.summary.draw) }}-{{ faNum(teamStats.summary.lost) }}</b>
              <small>برد-مساوی-باخت</small>
            </div>
            <div class="ss-item">
              <b>{{ faNum(teamStats.summary.goalsFor ?? '—') }}</b>
              <small>گل زده</small>
            </div>
            <div class="ss-item">
              <b>{{ faNum(teamStats.summary.goalsAgainst ?? '—') }}</b>
              <small>گل خورده</small>
            </div>
          </div>

          <!-- بهترین گلزنان و پاسورها -->
          <div class="stats-lists">
            <div v-if="teamStats.scorers.length" class="stat-list">
              <h3 class="section-title"><i class="fas fa-futbol"></i> بهترین گلزنان فصل</h3>
              <div
                  v-for="(s, i) in teamStats.scorers"
                  :key="'sc' + i"
                  class="stat-player-row"
                  @click="goPlayer(s.player)"
              >
                <span class="sp-rank">{{ faNum(i + 1) }}</span>
                <span class="sp-avatar" v-if="s.player.photo"><img :src="s.player.photo" :alt="s.player.name" loading="lazy"/></span>
                <span v-else class="sp-avatar initials" :style="{ background: avatarColor(s.player.name) }">{{ initials(s.player.name) }}</span>
                <span class="sp-name">{{ faPlayerName(s.player.name) }}</span>
                <b class="sp-val">{{ faNum(s.value) }} گل</b>
              </div>
            </div>

            <div v-if="teamStats.assists.length" class="stat-list">
              <h3 class="section-title"><i class="fas fa-hands-helping"></i> بهترین پاسورهای فصل</h3>
              <div
                  v-for="(s, i) in teamStats.assists"
                  :key="'as' + i"
                  class="stat-player-row"
                  @click="goPlayer(s.player)"
              >
                <span class="sp-rank">{{ faNum(i + 1) }}</span>
                <span class="sp-avatar" v-if="s.player.photo"><img :src="s.player.photo" :alt="s.player.name" loading="lazy"/></span>
                <span v-else class="sp-avatar initials" :style="{ background: avatarColor(s.player.name) }">{{ initials(s.player.name) }}</span>
                <span class="sp-name">{{ faPlayerName(s.player.name) }}</span>
                <b class="sp-val">{{ faNum(s.value) }} پاس</b>
              </div>
            </div>

            <div v-if="!teamStats.scorers.length && !teamStats.assists.length" class="empty-state">
              <i class="fas fa-info-circle"></i>
              <p>رویداد گل/پاسی برای این تیم ثبت نشده</p>
            </div>
          </div>
        </template>
      </div>

      <!-- ---------- تب جدول ---------- -->
      <div v-if="activeTab === 'standings'" class="tab-content">
        <div v-if="!standings" class="empty-state">
          <i class="fas fa-list-ol"></i>
          <p>جدولی برای این تیم پیدا نشد</p>
        </div>
        <template v-else>
          <h3 class="section-title">
            <i class="fas fa-list-ol"></i>
            {{ faLeagueName(standings.competition?.name) }}
            <small v-if="standings.season?.name">· فصل {{ standings.season.name }}</small>
          </h3>
          <div class="table-scroll">
            <table class="standings-table">
              <thead>
              <tr>
                <th>جایگاه</th><th class="team-col">تیم</th>
                <th>بازی</th><th>برد</th><th>مساوی</th><th>باخت</th>
                <th>تفاضل</th><th>امتیاز</th>
              </tr>
              </thead>
              <tbody>
              <tr
                  v-for="row in standings.rows"
                  :key="row.position"
                  :class="{ highlight: row.teamId === team.id }"
                  @click="row.slug && $router.push(`/team/${row.slug}`)"
              >
                <td>{{ faNum(row.position) }}</td>
                <td class="team-col">
                  <img v-if="row.logo" :src="row.logo"/>
                  <span>{{ faTeamName(row.name) }}</span>
                </td>
                <td>{{ faNum(row.played) }}</td>
                <td>{{ faNum(row.won) }}</td>
                <td>{{ faNum(row.draw) }}</td>
                <td>{{ faNum(row.lost) }}</td>
                <td>{{ faNum(row.goalDifference) }}</td>
                <td class="points">{{ faNum(row.points) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>

      <!-- ---------- تب اخبار ---------- -->
      <div v-if="activeTab === 'news'" class="tab-content">
        <div v-if="news.length === 0" class="empty-state">
          <i class="fas fa-newspaper"></i>
          <p>خبری پیدا نشد</p>
        </div>
        <div v-else class="news-grid">
          <article
              v-for="item in news"
              :key="item.id"
              class="news-card"
              @click="$router.push({ name: 'NewsDetail', params: { slug: item.slug } })"
          >
            <div class="news-img">
              <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title"
                   @error="e => e.target.style.display='none'"/>
              <div v-else class="news-no-img"><i class="fas fa-newspaper"></i></div>
              <span v-if="item.source" class="news-source-badge">{{ item.source.name }}</span>
            </div>
            <div class="news-body">
              <h4>{{ item.title }}</h4>
              <span class="news-time">{{ timeAgo(item.publishedAt) }}</span>
            </div>
          </article>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'
import { teamNames, leagueNamesLatin, roundLabel, playerNames, nationalityFa, coachNameFa as coachFaUtil } from '@/utils/translations'
import { setSeo } from '@/utils/seo'

const LIVE_STATUSES = ['LIVE', 'IN_PLAY', 'PAUSED'];
const FINISHED_STATUSES = ['FINISHED', 'AWARDED'];

const STATUS_FA = {
  SCHEDULED: 'برنامه‌ریزی‌شده',
  TIMED: 'زمان‌بندی‌شده',
  IN_PLAY: 'در جریان',
  LIVE: 'زنده',
  PAUSED: 'نیمه استراحت',
  FINISHED: 'پایان‌یافته',
  POSTPONED: 'به تعویق افتاده',
  SUSPENDED: 'متوقف‌شده',
  CANCELLED: 'لغو شده',
};

export default {
  name: 'TeamPage',

  data() {
    return {
      team: null,
      coach: null,
      players: [],
      matches: { recent: [], upcoming: [] },
      standings: null,
      news: [],
      squadSyncing: false,
      loading: true,
      error: null,
      activeTab: 'players',
      teamStats: null,
      teamStatsLoading: false,
      teamStatsLoaded: false,
    }
  },

  computed: {
    tabs() {
      const t = [
        { key: 'players', label: 'بازیکنان', icon: 'fa-users' },
        { key: 'matches', label: 'بازی‌ها', icon: 'fa-futbol' },
        { key: 'stats', label: 'آمار', icon: 'fa-chart-bar' },
      ];
      if (this.standings) t.push({ key: 'standings', label: 'جدول', icon: 'fa-list-ol' });
      t.push({ key: 'news', label: 'اخبار', icon: 'fa-newspaper' });
      return t;
    },

    faName() {
      return this.faTeamName(this.team?.name);
    },

    playerGroups() {
      const order = [
        { key: 'GOALKEEPER', label: 'دروازه‌بان‌ها' },
        { key: 'DEFENDER', label: 'مدافع‌ها' },
        { key: 'MIDFIELDER', label: 'هافبک‌ها' },
        { key: 'FORWARD', label: 'مهاجم‌ها' },
      ];
      const groupedIds = new Set();
      const groups = [];
      for (const g of order) {
        const ps = this.players.filter(p => p.position === g.key);
        ps.forEach(p => groupedIds.add(p.id));
        if (ps.length > 0) groups.push({ ...g, players: ps });
      }
      // بازیکنان بدون پست (سینک قدیمی یا پست ناشناخته) — نباید از لیست حذف شوند
      const rest = this.players.filter(p => !groupedIds.has(p.id));
      if (rest.length > 0) groups.push({ key: 'OTHER', label: 'بقیهٔ بازیکنان', players: rest });
      return groups;
    },
  },

  methods: {
    async loadTeam() {
      this.loading = true;
      this.error = null;
      try {
        const key = this.$route.params.id;
        const data = await apiService.request(`/teams/${key}`);
        if (!data) {
          setSeo({ notFound: true, title: 'تیم پیدا نشد | ضربه آزاد', path: `/team/${key}` });
          this.error = 'تیم پیدا نشد'; return;
        }
        this.team = data.team;
        this.coach = data.coach;
        this.players = data.players || [];
        this.matches = data.matches || { recent: [], upcoming: [] };
        this.standings = data.standings;
        this.news = data.news || [];
        this.squadSyncing = !!data.squadSyncing;
        const tName = this.team ? this.faTeamName(this.team.name) : '';
        if (tName) {
          setSeo({
            title: `${tName} | ترکیب، بازی‌ها و نتایج | ضربه آزاد`,
            description: `${tName}: ترکیب کامل بازیکنان، بازی‌های اخیر و پیش رو، جدول لیگ و اخبار مرتبط در ضربه آزاد`,
            image: (this.team && this.team.logo) || null,
            // URLهای قدیمی id-دار باید به نسخهٔ slug کانونیکال شوند تا گوگل
            // نسخهٔ تکراری را با URL استاندارد یکی کند (هم‌سو با سایت‌مپ/پیش‌رندر)
            path: `/team/${(this.team && this.team.slug) || key}`,
          });
        }
      } catch (e) {
        setSeo({ notFound: true, title: 'خطا در نمایش تیم | ضربه آزاد', path: `/team/${this.$route.params.id}` });
        this.error = 'خطا در دریافت اطلاعات تیم';
      } finally {
        this.loading = false;
      }
    },

    switchTab(key) {
      this.activeTab = key;
      // آمار تنبل: فقط با اولین باز شدن تب لود می‌شود
      if (key === 'stats' && !this.teamStatsLoaded) this.loadTeamStats();
    },

    async loadTeamStats() {
      if (this.teamStatsLoading) return;
      this.teamStatsLoading = true;
      try {
        const key = this.team?.slug || this.$route.params.id;
        this.teamStats = await apiService.getTeamStats(key);
        this.teamStatsLoaded = true;
      } catch (e) {
        this.teamStats = { empty: true, scorers: [], assists: [], summary: null };
        this.teamStatsLoaded = true;
      } finally {
        this.teamStatsLoading = false;
      }
    },

    goPlayer(p) {
      if (p && p.slug) this.$router.push(`/player/${p.slug}`);
    },

    // ---------- ترجمه‌ها ----------
    faTeamName(name) {
      return teamNames[name] || name || 'نامشخص';
    },
    coachNameFa(name) {
      return coachFaUtil(name);
    },
    faLeagueName(name) {
      return leagueNamesLatin[name] || name;
    },
    faPlayerName(name) {
      return playerNames[name] || name;
    },
    faCountry(name) {
      return nationalityFa[name] || name || '';
    },
    roundLabel(v) {
      return roundLabel(v);
    },
    statusLabel(status) {
      return STATUS_FA[status] || status;
    },

    // ---------- ابزار ----------
    isLive(m) {
      return LIVE_STATUSES.includes(m.status);
    },
    hasScore(m) {
      return this.isLive(m) || FINISHED_STATUSES.includes(m.status);
    },
    resultLabel(m) {
      const r = m.result;
      return r === 'W' ? 'برد' : r === 'L' ? 'باخت' : 'مساوی';
    },
    resultClass(m) {
      return { 'res-w': m.result === 'W', 'res-l': m.result === 'L', 'res-d': m.result === 'D' };
    },

    ageOf(birthDate) {
      if (!birthDate) return null;
      const d = new Date(birthDate);
      if (isNaN(d.getTime())) return null;
      return Math.floor((Date.now() - d.getTime()) / (365.25 * 864e5));
    },

    initials(name) {
      if (!name) return '؟';
      return name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },

    avatarColor(name) {
      let h = 0;
      for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) % 360;
      return `hsl(${h}, 45%, 38%)`;
    },

    /** آیکون گروه پست — OTHER هم باید آیکون داشته باشد وگرنه رندر کرش می‌کند */
    groupIcon(key) {
      const map = {
        GOALKEEPER: 'fa-hand-paper',
        DEFENDER: 'fa-shield-alt',
        MIDFIELDER: 'fa-chess-knight',
        FORWARD: 'fa-bolt',
        OTHER: 'fa-user',
      };
      return map[key] || 'fa-user';
    },

    faNum(value) {
      if (value === null || value === undefined) return '—';
      return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
    },

    faDate(dateStr) {
      if (!dateStr) return '—';
      try {
        return new Intl.DateTimeFormat(
            'fa-IR-u-ca-persian',
            { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'Asia/Tehran' }
        ).format(new Date(dateStr));
      } catch (e) { return '—'; }
    },

    faTime(dateStr) {
      if (!dateStr) return '—';
      try {
        return new Intl.DateTimeFormat(
            'fa-IR',
            { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tehran' }
        ).format(new Date(dateStr));
      } catch (e) { return '—'; }
    },

    timeAgo(dateStr) {
      if (!dateStr) return '';
      const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
      if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`;
      return `${Math.floor(diff / 86400)} روز پیش`;
    },
  },

  mounted() {
    this.loadTeam();
  },

  watch: {
    '$route.params.id'() {
      this.loadTeam();
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.team-page {
  width: 100%;
  min-height: 100vh;
  background: $gray-100;
  direction: rtl;
  font-family: $font-family;
}

.team-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 24px 60px;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 120px 20px;
  color: $gray-500;

  &.error i { color: #dc2626; font-size: 2rem; }
}

.back-btn {
  background: $secondary-blue;
  color: #fff;
  padding: 8px 20px;
  border-radius: 8px;
  text-decoration: none;
}

.spinner {
  width: 42px; height: 42px;
  border: 4px solid $gray-200;
  border-top-color: $secondary-blue;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

// ---------- breadcrumb ----------
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: $gray-500;
  margin-bottom: 14px;

  a { color: $secondary-blue; text-decoration: none; &:hover { text-decoration: underline; } }
  i { font-size: 0.6rem; }
}

// ---------- hero ----------
.team-hero {
  background: 
    url('~@/assets/images/header-ball-bg.svg') no-repeat left 4% center / 260px auto,
    linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  border-radius: 14px;
  padding: 24px;
  color: #fff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;

  @include mobile {
    background-position: left -30px center;
    background-size: 180px auto;
  }
}

.hero-main {
  display: flex;
  align-items: center;
  gap: 18px;
}

.hero-logo {
  width: 84px;
  height: 84px;
  object-fit: contain;
  background: #fff;
  border-radius: 14px;
  padding: 6px;
}

.hero-name {
  margin: 0 0 10px;
  font-size: 1.5rem;
}

/* نشان «تیم ملی» کنار نام تیم‌های ملی */
.nat-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  vertical-align: middle;

  background: rgba(66, 211, 146, 0.18);
  border: 1px solid rgba(66, 211, 146, 0.5);
  color: #42d392;

  font-size: 0.68rem;
  font-weight: 700;

  padding: 4px 10px;
  border-radius: 999px;
  margin-right: 8px;
  transform: translateY(-3px);
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.85);

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i { font-size: 0.75rem; opacity: 0.8; }
    small { opacity: 0.75; }
  }
}

.flag {
  width: 18px;
  height: 13px;
  object-fit: cover;
  border-radius: 2px;
}

.coach-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 12px 18px;

  i { font-size: 1.4rem; opacity: 0.9; }
}

.coach-info {
  display: flex;
  flex-direction: column;
}

.coach-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
}

.coach-name { font-weight: 600; }

// حالت لینک (router-link تگ a رندر می‌کند) — رنگ و خط‌زیر پیش‌فرض لینک خنثی شود
a.coach-card {
  color: inherit;
  text-decoration: none;

  &:hover .coach-name { text-decoration: underline; }
}

.coach-arrow {
  font-size: 0.7rem;
  opacity: 0.55;
  margin-right: 4px;
}

// ---------- sync banner ----------
.sync-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 0.82rem;
  margin-bottom: 16px;
}

// ---------- tabs ----------
.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  border: 1px solid $gray-200;
  color: $gray-500;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-family: $font-family;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: $secondary-blue; color: $secondary-blue; }

  &.active {
    background: $secondary-blue;
    border-color: $secondary-blue;
    color: #fff;
  }
}

.tab-content { animation: fadeIn 0.2s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }

// ---------- players ----------
.position-group { margin-bottom: 22px; }

.group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  color: $gray-700;
  margin: 0 0 10px;

  small { color: $gray-400; font-weight: 400; }
}

.players-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.player-card {
  position: relative;
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 12px;
  padding: 16px 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s;

  &:hover {
    border-color: $secondary-blue;
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  }
}

.player-number {
  position: absolute;
  top: 8px;
  inset-inline-start: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  color: $gray-400;
}

.player-avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.player-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.player-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: $gray-700;
  line-height: 1.4;
}

.player-sub {
  font-size: 0.68rem;
  color: $gray-400;
}

// ---------- match cards ----------
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  color: $gray-700;
  margin: 18px 0 10px;

  small { color: $gray-400; font-weight: 400; }
}

.dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.live-dot { background: #ef4444; }
.done-dot { background: #22c55e; }

.match-card {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover { border-color: $secondary-blue; }
  &.live { border-color: #ef4444; }
}

.round-date {
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  color: $gray-400;
  margin-bottom: 8px;

  .round, .comp {
    background: $gray-100;
    border-radius: 6px;
    padding: 2px 8px;
  }
}

.row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;

  .side {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    color: $gray-700;
    min-width: 0;

    &:last-child { justify-content: flex-end; text-align: left; }
    img { width: 24px; height: 24px; object-fit: contain; }
    .winner { color: #16a34a; }
  }

  .center {
    text-align: center;

    strong { font-size: 1.05rem; }
    small {
      display: block;
      font-size: 0.68rem;
      color: $gray-400;
    }
    .live-label { color: #ef4444; font-weight: 700; }
    .res-w { color: #16a34a; }
    .res-l { color: #dc2626; }
    .res-d { color: $gray-400; }
  }
}

// ---------- standings ----------
.table-scroll {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 12px;
  overflow-x: auto;
}

.standings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  min-width: 560px;

  th, td { padding: 9px 12px; text-align: center; }

  th {
    background: $gray-50;
    color: $gray-500;
    font-weight: 600;
    font-size: 0.72rem;
  }

  tbody tr {
    border-top: 1px solid $gray-100;
    cursor: pointer;

    &:hover { background: $gray-50; }
    &.highlight {
      background: #eff6ff;
      td.team-col span { font-weight: 700; color: $secondary-blue; }
    }
  }

  .team-col {
    text-align: right;

    display: flex;
    align-items: center;
    gap: 8px;

    img { width: 22px; height: 22px; object-fit: contain; }
  }

  .points { font-weight: 700; }
}

/* موبایل: جدول رده‌بندی خلاصه — برد/مساوی/باخت حذف تا اسکرول افقی نخوریم */
@media (max-width: 768px) {
  .standings-table {
    min-width: 0;

    th, td { padding: 8px 5px; }

    th:nth-child(4), td:nth-child(4),
    th:nth-child(5), td:nth-child(5),
    th:nth-child(6), td:nth-child(6) {
      display: none;
    }

    .team-col {
      gap: 6px;
      img { width: 20px; height: 20px; }
    }
  }
}

// ---------- news ----------
.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.news-card {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: $secondary-blue;
    transform: translateY(-2px);
  }
}

.news-img {
  position: relative;
  aspect-ratio: 16/9;
  background: $gray-100;

  img { width: 100%; height: 100%; object-fit: cover; }
}

.news-no-img {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: $gray-300; font-size: 1.6rem;
}

.news-source-badge {
  position: absolute;
  bottom: 8px;
  inset-inline-start: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.65rem;
  padding: 2px 8px;
  border-radius: 6px;
}

.news-body {
  padding: 12px 14px;

  h4 {
    margin: 0 0 8px;
    font-size: 0.82rem;
    line-height: 1.6;
    color: $gray-700;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.news-time { font-size: 0.68rem; color: $gray-400; }

// ---------- empty ----------
.empty-state {
  background: #fff;
  border: 1px dashed $gray-200;
  border-radius: 12px;
  padding: 48px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: $gray-400;

  i { font-size: 1.8rem; }
  p { margin: 0; font-size: 0.85rem; }
}

// ---------- responsive ----------
@media (max-width: 768px) {
  .team-container { padding: 14px 14px 40px; }

  .team-hero { padding: 18px; }
  .hero-logo { width: 60px; height: 60px; }
  .hero-name { font-size: 1.15rem; }

  .players-grid { grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; }

  .row .side span { font-size: 0.75rem; }
}

// ---------- تب آمار ----------
.ss-source {
  grid-column: 1 / -1;
  margin: 0 0 4px;
  color: #6b7280;
  font-size: 11.5px;

  i { color: #3b82f6; margin-left: 4px; font-size: 10.5px; }
}
.season-summary {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 16px;

  .ss-item {
    background: #fff;
    border-radius: 12px;
    padding: 12px 6px;
    text-align: center;
    box-shadow: $shadow-sm;

    b {
      display: block;
      font-size: 1.05rem;
      color: $primary-blue;
      margin-bottom: 3px;
    }

    small { color: $gray-500; font-size: 0.68rem; }
  }
}

.stats-lists {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  .stat-list {
    background: #fff;
    border-radius: 12px;
    padding: 14px;
    box-shadow: $shadow-sm;
  }
}

.stat-player-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 10px;
  cursor: pointer;
  transition: background .15s;

  &:hover { background: $gray-50; }

  .sp-rank {
    width: 20px;
    color: $gray-400;
    font-weight: 700;
    font-size: 0.8rem;
    flex-shrink: 0;
    text-align: center;
  }

  .sp-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;

    img { width: 100%; height: 100%; object-fit: cover; }

    &.initials {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 0.72rem;
      font-weight: 700;
    }
  }

  .sp-name {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    color: $gray-800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sp-val {
    color: #059669;
    font-size: 0.8rem;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .season-summary { grid-template-columns: repeat(3, 1fr); }
  .stats-lists { grid-template-columns: 1fr; }
}
</style>
